import { useEffect, useId, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { Question } from '../../config/types';
import { useFormData } from '../../hooks/useFormData';
import {
  chainValidations,
  checkAllFieldsHaveValues,
  groupValidation,
  objectToString,
  stringToObject,
  sumRow,
} from '../../utils/form-utils';
import { dateIsEmpty } from './DateInputField/utils';
import Details from './Details';
import ErrorMessage from './ErrorMessage';
import ExceptionBreakdownColumn from './ExceptionBreakdownColumn';
interface GradeInputProps {
  name: string;
  defaultValue?: string;
  disabled?: boolean;
  anchorId?: string;
  onChange?(value: string): void;
}

export const getColumns = () => [
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
  'Ten',
];
export const getRows = () => ['scs2', 'scs1', 'grade6', 'grade7', 'seo', 'heo', 'eo', 'ao', 'aa'];

export const getKeys = () => {
  const columns = getColumns();
  const rows = getRows();
  return rows.flatMap((row) => columns.map((column) => `${row}${column}`));
};

export function ExceptionBreakdownInputWrapperColumn({
  defaultValue,
  anchorId = useId(),
  onChange,
}: GradeInputProps) {
  const generateInputId = (suffix: string) => `${anchorId}-${suffix}`;

  const initialValues = useMemo(() => stringToObject(defaultValue), [defaultValue]);

  const [values, setValues] = useState(initialValues);
  const [rowTotals, setRowTotals] = useState<Record<string, number>>({});

  const triggerChange = (updatedValues: typeof values) => {
    if (onChange) {
      const valueString = objectToString(updatedValues);
      if (dateIsEmpty(valueString)) {
        onChange('');
      } else {
        onChange(valueString);
      }
    }
  };

  // Type the field argument to only accept valid keys of 'values'
  const handleValueChange = (field: keyof typeof values) => (newValue: string) => {
    const updatedValues = { ...values, [field]: newValue };
    const rows = ['scs2', 'scs1', 'grade6', 'grade7', 'seo', 'heo', 'eo', 'ao', 'aa'];
    const rowTotals = rows.reduce(
      (acc, row) => {
        const total = sumRow(row, updatedValues);
        acc[row] = total;
        return acc;
      },
      {} as Record<string, number>,
    );
    setRowTotals(rowTotals);
    setValues(updatedValues);
    triggerChange(updatedValues);
  };

  const getRowTotal = (row: string) => {
    const total = rowTotals?.[row];
    return total && total !== 0 ? total.toString() : '';
  };

  const gradeRows = useMemo(
    () => [
      {
        grade: 'SCS 2',
        fields: [
          'scs2One',
          'scs2Two',
          'scs2Three',
          'scs2Four',
          'scs2Five',
          'scs2Six',
          'scs2Seven',
          'scs2Eight',
          'scs2Nine',
          'scs2Ten',
        ],
        disabled: ['scs2Ten'],
      },
      {
        grade: 'SCS 1',
        fields: [
          'scs1One',
          'scs1Two',
          'scs1Three',
          'scs1Four',
          'scs1Five',
          'scs1Six',
          'scs1Seven',
          'scs1Eight',
          'scs1Nine',
          'scs1Ten',
        ],
        disabled: ['scs1Ten'],
      },
      {
        grade: 'Grade 6',
        fields: [
          'grade6One',
          'grade6Two',
          'grade6Three',
          'grade6Four',
          'grade6Five',
          'grade6Six',
          'grade6Seven',
          'grade6Eight',
          'grade6Nine',
          'grade6Ten',
        ],
        disabled: ['grade6Ten'],
      },
      {
        grade: 'Grade 7',
        fields: [
          'grade7One',
          'grade7Two',
          'grade7Three',
          'grade7Four',
          'grade7Five',
          'grade7Six',
          'grade7Seven',
          'grade7Eight',
          'grade7Nine',
          'grade7Ten',
        ],
        disabled: ['grade7Ten'],
      },
      {
        grade: 'SEO',
        fields: [
          'seoOne',
          'seoTwo',
          'seoThree',
          'seoFour',
          'seoFive',
          'seoSix',
          'seoSeven',
          'seoEight',
          'seoNine',
          'seoTen',
        ],
        disabled: ['seoTen'],
      },
      {
        grade: 'HEO',
        fields: [
          'heoOne',
          'heoTwo',
          'heoThree',
          'heoFour',
          'heoFive',
          'heoSix',
          'heoSeven',
          'heoEight',
          'heoNine',
          'heoTen',
        ],
        disabled: ['heoTen'],
      },
      {
        grade: 'EO',
        fields: [
          'eoOne',
          'eoTwo',
          'eoThree',
          'eoFour',
          'eoFive',
          'eoSix',
          'eoSeven',
          'eoEight',
          'eoNine',
          'eoTen',
        ],
      },
      {
        grade: 'AO',
        fields: [
          'aoOne',
          'aoTwo',
          'aoThree',
          'aoFour',
          'aoFive',
          'aoSix',
          'aoSeven',
          'aoEight',
          'aoNine',
          'aoTen',
        ],
      },
      {
        grade: 'AA',
        fields: [
          'aaOne',
          'aaTwo',
          'aaThree',
          'aaFour',
          'aaFive',
          'aaSix',
          'aaSeven',
          'aaEight',
          'aaNine',
          'aaTen',
        ],
      },
    ],
    [],
  );

  return (
    <div>
      <table className="govuk-table">
        <thead className="govuk-table__head">
          <tr className="govuk-table__row">
            <th scope="col" className="govuk-table__header" style={{ width: '100px' }}>
              Grade
            </th>
            <th scope="col" className="govuk-table__header">
              1
            </th>
            <th scope="col" className="govuk-table__header">
              2
            </th>
            <th scope="col" className="govuk-table__header">
              3
            </th>
            <th scope="col" className="govuk-table__header">
              4
            </th>
            <th scope="col" className="govuk-table__header">
              5
            </th>
            <th scope="col" className="govuk-table__header">
              6
            </th>
            <th scope="col" className="govuk-table__header">
              7
            </th>
            <th scope="col" className="govuk-table__header">
              8
            </th>
            <th scope="col" className="govuk-table__header">
              9
            </th>
            <th scope="col" className="govuk-table__header">
              10
            </th>
            <th scope="col" className="govuk-table__header">
              Total
            </th>
          </tr>
        </thead>
        <tbody className="govuk-table__body">
          {gradeRows.map(({ grade, fields, disabled }) => (
            <tr key={grade} className="govuk-table__row">
              <th scope="row" className="govuk-table__header">
                {grade}
              </th>
              {fields.map((field) => (
                <td key={field} className="govuk-table__cell">
                  <ExceptionBreakdownColumn
                    testId={`${field}-${grade.toLowerCase().replace(' ', '-')}`}
                    disabled={disabled?.includes(field)}
                    anchorId={generateInputId(field)}
                    onChange={handleValueChange(field as keyof typeof values)} // Cast field to a valid key
                    value={values[field as keyof typeof values]} // Cast field to a valid key
                  />
                </td>
              ))}
              <td
                key={`total-${grade.toLowerCase().replace(' ', '-')}`}
                className="govuk-table__cell"
              >
                <ExceptionBreakdownColumn
                  testId={`${grade.toLowerCase().replace(' ', '-')}-total`}
                  disabled={true}
                  anchorId={generateInputId(grade.toLowerCase().replace(' ', ''))}
                  value={getRowTotal(grade.toLowerCase().replace(' ', ''))}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface GradeInputFieldProps {
  question: Question;
  disabled?: boolean;
  anchorId: string;
  detailsHeading?: string;
  detailsContent?: string;
}

const keysToIgnoreInValidation = [
  'scs2Ten',
  'scs1Ten',
  'grade6Ten',
  'grade7Ten',
  'seoTen',
  'heoTen',
];

export function ExceptionBreakdownInput({
  question,
  disabled,
  anchorId,
  detailsHeading,
  detailsContent,
}: GradeInputFieldProps) {
  const { register, getFieldState, getValues, setValue } = useFormContext();
  const { error } = getFieldState(question.name);

  useEffect(() => {
    if (getValues(question.name) === undefined) {
      setValue(question.name, '');
    }
  }, [getValues, question.name, setValue]);

  const { data } = useFormData();
  const { onChange, name } = register(question.name, {
    validate: (value: string) =>
      chainValidations([
        checkAllFieldsHaveValues({
          value: stringToObject(value),
          keys: getKeys().filter((x) => !keysToIgnoreInValidation.includes(x)),
        }),
        groupValidation({
          value: stringToObject(value),
          answers: data?.answers,
          question,
        }),
      ]),
  });
  const handleChange = (dateChange: string) => {
    onChange({ target: { name, value: dateChange } });
  };

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}
      <div className="govuk-date-input">
        <ExceptionBreakdownInputWrapperColumn
          name={question.name}
          disabled={disabled}
          anchorId={anchorId}
          onChange={handleChange}
          defaultValue={getValues(question.name)}
        />
      </div>
    </>
  );
}
